import React, { useState, useEffect } from 'react';
import { Search, Heart, User, ShoppingCart, Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useCartStore } from '../stores/cartStore';
import { useCartAnimationStore } from '../stores/cartAnimationStore';
import { formatMoney } from '../shared/lib/money';

import { useSettingsStore } from '../stores/settingsStore';

const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoveredNav, setHoveredNav] = useState<string | null>(null);
  const [expandedMobileNav, setExpandedMobileNav] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { phoneNumber, email } = useSettingsStore();
  const { getTotals, getItemCount } = useCartStore();
  const { cartBounce } = useCartAnimationStore();
  const navigate = useNavigate();
  const totals = getTotals();
  const itemCount = getItemCount();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const categories = useSettingsStore(state => state.categories);

  const navItems = React.useMemo(() => {
    const dynamicItems = (categories || []).map(cat => ({
      label: cat.name,
      hasChildren: cat.subcategories && cat.subcategories.length > 0,
      href: `/kategori/${cat.categorySlug}`,
      children: (cat.subcategories || []).map((sub: any) => ({
        label: sub.name,
        href: `/kategori/${cat.categorySlug}/${sub.categorySlug}`
      }))
    }));
    return [
      ...dynamicItems,
      { label: 'Fırsatlar', hasChildren: false, href: '/arama?q=indirimli' },
    ];
  }, [categories]);

  const closeMobileMenu = () => setMobileOpen(false);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/arama?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  return (
    <>
      {/* TOP DARK BAR */}
      <div className="bg-[#1a1f2e] text-white hidden lg:block text-xs">
        <div className="max-w-[1320px] mx-auto px-4 flex items-center justify-between h-[36px]">
          <div className="flex items-center gap-5">
            <div className="flex items-center gap-2">
              <span className="font-bold tracking-[0.15em] text-[13px]">DAIKIN</span>
              <span className="bg-emerald-600 text-white text-[9px] font-semibold px-2 py-[2px] rounded-sm tracking-wide">Yetkili Satıcı</span>
            </div>
            <span className="w-px h-4 bg-white/20" />
            <a href={`tel:${phoneNumber.replace(/[^0-9+]/g, '')}`} className="flex items-center gap-1.5 text-white/70 hover:text-white transition-colors">
              <Phone className="w-3 h-3" />
              <span>Müşteri Hizmetleri: <strong className="text-white">{phoneNumber}</strong></span>
            </a>
          </div>
          <div className="flex items-center gap-5 text-white/60">
            <Link to="/hakkimizda" className="hover:text-white transition-colors text-[11px]">Biz Kimiz?</Link>
            <Link to="/iletisim" className="hover:text-white transition-colors text-[11px]">İletişim</Link>
            <Link to="/sss" className="hover:text-white transition-colors text-[11px]">Yardım</Link>
          </div>
        </div>
      </div>

      {/* ANNOUNCEMENT MARQUEE */}
      <div className="bg-gradient-to-r from-[#0891b2] to-[#0e7490] text-white overflow-hidden">
        <div className="marquee-container py-[5px]">
          <div className="marquee-content">
            {[...Array(6)].map((_, i) => (
              <React.Fragment key={i}>
                <span className="text-[11px] font-medium px-6 whitespace-nowrap">10.000 TL ve Üzeri Siparişlerde Kargo Ücretsiz</span>
                <span className="text-white/40">•</span>
                <span className="text-[11px] font-medium px-6 whitespace-nowrap">Havale / EFT ile %12 İndirim</span>
                <span className="text-white/40">•</span>
                <span className="text-[11px] font-medium px-6 whitespace-nowrap">Peşin Fiyatına 12 Taksit</span>
                <span className="text-white/40">•</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* MAIN HEADER */}
      <header className={`sticky top-0 z-50 bg-white transition-shadow duration-300 ${scrolled ? 'shadow-[0_2px_20px_rgba(0,0,0,0.08)]' : ''}`}>
        <div className="max-w-[1320px] mx-auto px-3 lg:px-4">
          <div className="flex items-center h-[56px] lg:h-[60px] gap-2 lg:gap-6">
            {/* Mobile: hamburger + search */}
            <div className="flex items-center gap-0.5 lg:hidden">
              <button
                className="p-1.5 text-surface-700 hover:bg-surface-50 rounded-lg transition-colors"
                onClick={() => setMobileOpen(true)}
                aria-label="Menüyü aç"
                type="button"
              >
                <Menu className="w-6 h-6" />
              </button>
              <Link to="/arama" className="p-1.5 text-surface-600 hover:bg-surface-50 rounded-lg transition-colors">
                <Search className="w-5 h-5" />
              </Link>
            </div>

            {/* LOGO */}
            <Link to="/" className="flex items-center shrink-0 lg:mr-2 mx-auto lg:mx-0">
              <span className="text-[20px] lg:text-[22px] font-extrabold tracking-tight">
                <span className="text-surface-900">AR</span>{' '}
                <span className="text-[#0891b2]">KLİMA</span>
              </span>
            </Link>

            {/* NAVIGATION (Desktop) */}
            <nav className="hidden lg:flex items-center gap-0 h-full">
              {navItems.map((item) => (
                <div
                  key={item.label}
                  className="relative h-full flex items-center"
                  onMouseEnter={() => item.hasChildren && setHoveredNav(item.label)}
                  onMouseLeave={() => setHoveredNav(null)}
                >
                  {item.hasChildren ? (
                    <button
                      className="flex items-center gap-1 px-3 h-full text-[13px] font-medium text-surface-700 hover:text-[#0891b2] transition-colors whitespace-nowrap"
                    >
                      {item.label}
                      <ChevronDown className={`w-3 h-3 transition-transform ${hoveredNav === item.label ? 'rotate-180' : ''}`} />
                    </button>
                  ) : (
                    <Link
                      to={item.href || '#'}
                      className="flex items-center gap-1 px-3 h-full text-[13px] font-medium text-surface-700 hover:text-[#0891b2] transition-colors whitespace-nowrap"
                    >
                      {item.label}
                    </Link>
                  )}
                  {item.hasChildren && hoveredNav === item.label && (
                    <div className="absolute top-full left-0 bg-white shadow-xl rounded-b-xl border border-surface-100 py-2 min-w-[220px] z-50">
                      {item.children?.map((child) => (
                        <Link
                          key={child.label}
                          to={child.href}
                          className="block px-4 py-2 text-sm text-surface-600 hover:bg-[#ecfeff] hover:text-[#0891b2] transition-colors"
                          onClick={() => setHoveredNav(null)}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Search bar desktop */}
            <form onSubmit={handleSearch} className="hidden lg:flex flex-1 max-w-sm mx-4">
              <div className="relative w-full">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Ürün ara…"
                  className="w-full pl-4 pr-10 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
                <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-cyan-600">
                  <Search className="w-4 h-4" />
                </button>
              </div>
            </form>

            {/* RIGHT ACTIONS */}
            <div className="flex items-center gap-0.5 lg:ml-auto">
              <Link to="/favoriler" className="p-2 lg:p-2.5 text-surface-600 hover:text-[#0891b2] transition-colors rounded-lg hover:bg-surface-50 flex items-center">
                <Heart className="w-5 h-5" />
              </Link>
              <Link to="/giris" className="p-2 lg:p-2.5 text-surface-600 hover:text-[#0891b2] transition-colors rounded-lg hover:bg-surface-50 hidden sm:flex items-center">
                <User className="w-5 h-5" />
              </Link>
              {/* Cart button */}
              <Link id="cart-btn" to="/sepet" className={`flex items-center gap-2 bg-[#0891b2] hover:bg-[#0e7490] text-white rounded-lg px-3 py-2 transition-all ml-1 lg:ml-1.5 relative${cartBounce ? ' cart-btn-bounce' : ''}`}>
                <ShoppingCart className={`w-[18px] h-[18px]${cartBounce ? ' cart-icon-shake' : ''}`} />
                {itemCount > 0 && (
                  <span className={`absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center${cartBounce ? ' cart-badge-pop' : ''}`}>
                    {itemCount}
                  </span>
                )}
                <div className="hidden sm:flex flex-col leading-tight">
                  <span className="text-[10px] font-normal opacity-80">{itemCount} Ürün</span>
                  <span className="text-[13px] font-bold">{formatMoney(totals.total)}</span>
                </div>
                <span className="sm:hidden text-[13px] font-bold">{itemCount}</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* MOBILE MENU DRAWER */}
      <div
        className={`fixed inset-0 bg-black/50 z-[59] lg:hidden transition-opacity duration-300 ${mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={closeMobileMenu}
        aria-hidden="true"
      />

      <div
        className={`fixed top-0 left-0 w-[300px] h-full bg-white shadow-2xl overflow-y-auto z-[60] lg:hidden transition-transform duration-300 ease-in-out ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
        role="dialog"
        aria-modal="true"
        aria-label="Ana Menü"
      >
        <div className="p-4 border-b border-surface-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <Link to="/" onClick={closeMobileMenu} className="text-lg font-extrabold">
            <span className="text-surface-900">AR</span>{' '}
            <span className="text-[#0891b2]">KLİMA</span>
          </Link>
          <button onClick={closeMobileMenu} className="p-1.5 hover:bg-surface-50 rounded-lg transition-colors" type="button">
            <X className="w-5 h-5 text-surface-600" />
          </button>
        </div>

        <div className="p-3">
          {navItems.map((item) => (
            <div key={item.label}>
              {item.hasChildren ? (
                <button
                  type="button"
                  onClick={() => setExpandedMobileNav(expandedMobileNav === item.label ? null : item.label)}
                  className="w-full flex items-center justify-between px-3 py-3 text-sm font-medium text-surface-700 hover:bg-[#ecfeff] hover:text-[#0891b2] rounded-lg transition-colors"
                >
                  <span>{item.label}</span>
                  <ChevronDown className={`w-4 h-4 text-surface-400 transition-transform duration-200 ${expandedMobileNav === item.label ? 'rotate-180' : ''}`} />
                </button>
              ) : (
                <Link
                  to={item.href || '#'}
                  onClick={closeMobileMenu}
                  className="w-full flex items-center px-3 py-3 text-sm font-medium text-surface-700 hover:bg-[#ecfeff] hover:text-[#0891b2] rounded-lg transition-colors"
                >
                  {item.label}
                </Link>
              )}
              {item.hasChildren && expandedMobileNav === item.label && (
                <div className="ml-3 pl-3 border-l-2 border-[#0891b2]/20 mb-2 space-y-0.5">
                  {item.children?.map((child) => (
                    <Link
                      key={child.label}
                      to={child.href}
                      onClick={closeMobileMenu}
                      className="block px-3 py-2 text-sm text-surface-500 hover:text-[#0891b2] hover:bg-[#ecfeff] rounded-lg transition-colors"
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div className="mt-4 pt-4 border-t border-surface-100 space-y-1">
            <Link to="/hakkimizda" onClick={closeMobileMenu} className="block px-3 py-2.5 text-sm text-surface-500 hover:text-[#0891b2] hover:bg-[#ecfeff] rounded-lg transition-colors">Hakkımızda</Link>
            <Link to="/iletisim" onClick={closeMobileMenu} className="block px-3 py-2.5 text-sm text-surface-500 hover:text-[#0891b2] hover:bg-[#ecfeff] rounded-lg transition-colors">İletişim</Link>
            <Link to="/sss" onClick={closeMobileMenu} className="block px-3 py-2.5 text-sm text-surface-500 hover:text-[#0891b2] hover:bg-[#ecfeff] rounded-lg transition-colors">Yardım</Link>

          </div>

          <div className="p-4 border-t border-gray-100 bg-gray-50 flex flex-col gap-3">
            <a href={`tel:${phoneNumber.replace(/[^0-9+]/g, '')}`} className="flex items-center gap-3 text-sm text-gray-600">
              <div className="w-8 h-8 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center">
                <Phone className="w-4 h-4" />
              </div>
              <span className="font-medium">Müşteri Hizmetleri<br/><strong className="text-surface-900">{phoneNumber}</strong></span>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
